import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, TrashIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import toast from 'react-hot-toast';

const CommentItem = ({ comment, onDelete, onLike, onReply, currentUserId, currentUserRole, isReply = false, parentCommentUser = null, rootCommentId = null }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [showReplies, setShowReplies] = useState(false);
    
    // Handle both string and object comparison for likes
    const isLiked = comment.likes?.some(likeId => {
        const likeIdStr = typeof likeId === 'object' ? String(likeId) : String(likeId);
        const userIdStr = String(currentUserId);
        return likeIdStr === userIdStr;
    }) || false;
    
    const isOwner = comment.user?._id === currentUserId;
    const canDelete = isOwner || currentUserRole === 'admin' || currentUserRole === 'editor';
    
    // Use rootCommentId if it exists (we're in nested replies), otherwise use this comment's ID (we're at root level)
    const mainCommentId = rootCommentId || comment._id;

    const formatDate = (date) => {
        const now = new Date();
        const commentDate = new Date(date);
        const diffMs = now - commentDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return commentDate.toLocaleDateString();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${isReply ? 'ml-12 mt-3' : 'mb-6'}`}
        >
            <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-600 flex items-center justify-center text-white font-bold">
                        {comment.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                </div>

                {/* Comment Content */}
                <div className="flex-1 min-w-0">
                    <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm">
                                    {comment.user?.name || 'Anonymous'}
                                </span>
                                {(comment.user?.role === 'admin' || comment.user?.role === 'editor') && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        comment.user.role === 'admin' 
                                            ? 'bg-red-500 text-white' 
                                            : 'bg-blue-500 text-white'
                                    }`}>
                                        {comment.user.role === 'admin' ? 'Admin' : 'Editor'}
                                    </span>
                                )}
                                <span className="text-xs text-light-text/60 dark:text-dark-text/60">
                                    {formatDate(comment.createdAt)}
                                </span>
                            </div>
                            {canDelete && (
                                <button
                                    onClick={() => onDelete(comment._id)}
                                    className="text-red-500 hover:text-red-700 transition-colors"
                                    title={isOwner ? "Delete comment" : "Delete comment (moderator)"}
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-light-text dark:text-dark-text whitespace-pre-wrap break-words">
                            {comment.content}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-4 mt-2 ml-2">
                        <button
                            onClick={() => onLike(comment._id)}
                            className="flex items-center gap-1 text-xs text-light-text/70 dark:text-dark-text/70 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                            {isLiked ? (
                                <HeartIconSolid className="h-4 w-4 text-red-500" />
                            ) : (
                                <HeartIcon className="h-4 w-4" />
                            )}
                            <span>{comment.likeCount || 0}</span>
                        </button>

                        <button
                            onClick={() => setShowReplyForm(!showReplyForm)}
                            className="flex items-center gap-1 text-xs text-light-text/70 dark:text-dark-text/70 hover:text-light-primary dark:hover:text-dark-primary transition-colors"
                        >
                            <ChatBubbleLeftIcon className="h-4 w-4" />
                            <span>Reply</span>
                        </button>

                        {!isReply && comment.replies && comment.replies.length > 0 && (
                            <button
                                onClick={() => setShowReplies(!showReplies)}
                                className="text-xs text-light-primary dark:text-dark-primary font-semibold"
                            >
                                {showReplies ? 'Hide' : 'Show'} {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                            </button>
                        )}
                    </div>

                    {/* Reply Form */}
                    <AnimatePresence>
                        {showReplyForm && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3"
                            >
                                <CommentForm
                                    onSubmit={(content) => {
                                        onReply(content, mainCommentId);
                                        setShowReplyForm(false);
                                    }}
                                    onCancel={() => setShowReplyForm(false)}
                                    placeholder="Write a reply..."
                                    replyingTo={comment.user?.name}
                                    small
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Replies */}
                    <AnimatePresence>
                        {showReplies && comment.replies && comment.replies.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                {comment.replies.map(reply => (
                                    <CommentItem
                                        key={reply._id}
                                        comment={reply}
                                        onDelete={onDelete}
                                        onLike={onLike}
                                        onReply={onReply}
                                        currentUserId={currentUserId}
                                        currentUserRole={currentUserRole}
                                        parentCommentUser={comment.user}
                                        rootCommentId={mainCommentId}
                                        isReply
                                    />
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

const CommentForm = ({ onSubmit, onCancel, placeholder = "Write a comment...", small = false, replyingTo = null }) => {
    const [content, setContent] = useState(replyingTo ? `@${replyingTo} ` : '');
    const [submitting, setSubmitting] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textareaRef = React.useRef(null);

    React.useEffect(() => {
        if (replyingTo && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(content.length, content.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replyingTo]);

    const emojis = [
        '😀', '😂', '😍', '🤔', '👍', '👎', '❤️', '🔥', '✨', '🎉',
        '😊', '😎', '🤗', '😢', '😡', '🙏', '💪', '👏', '🎯', '⭐',
        '💡', '📚', '✅', '❌', '⚠️', '📝', '🚀', '💻', '🔬', '🌟'
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;

        setSubmitting(true);
        await onSubmit(content);
        setContent('');
        setSubmitting(false);
    };

    const insertEmoji = (emoji) => {
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = content.substring(0, start) + emoji + content.substring(end);
        setContent(newContent);
        setShowEmojiPicker(false);
        
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-2">
            <div className="relative">
                <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={placeholder}
                    maxLength={2000}
                    rows={small ? 2 : 3}
                    className="input-field resize-none"
                    disabled={submitting}
                />
                
                {/* Emoji Picker Dropdown */}
                <AnimatePresence>
                    {showEmojiPicker && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-50 w-80"
                        >
                            <div className="grid grid-cols-10 gap-2 max-h-48 overflow-y-auto">
                                {emojis.map((emoji, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => insertEmoji(emoji)}
                                        className="text-2xl hover:scale-125 transition-transform cursor-pointer p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {/* Emoji Button */}
                    <button
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="text-xl hover:scale-110 transition-transform p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title="Add emoji"
                    >
                        😊
                    </button>
                    <span className="text-xs text-light-text/60 dark:text-dark-text/60">
                        {content.length}/2000
                    </span>
                </div>
                <div className="flex gap-2">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary !py-1 !px-3 text-sm"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        className="btn-primary !py-1 !px-3 text-sm"
                        disabled={submitting || !content.trim()}
                    >
                        {submitting ? 'Posting...' : 'Post'}
                    </button>
                </div>
            </div>
        </form>
    );
};

const CommentsSection = ({ articleId }) => {
    const { user, token } = useContext(AuthContext);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchComments = React.useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/comments/${articleId}`);
            setComments(res.data);
        } catch (err) {
            console.error('Error fetching comments:', err);
        } finally {
            setLoading(false);
        }
    }, [articleId]);

    React.useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleAddComment = async (content, parentCommentId = null) => {
        if (!token) {
            window.location.href = '/login';
            return;
        }

        try {
            const config = { 
                headers: { 
                    'Content-Type': 'application/json',
                    'x-auth-token': token 
                } 
            };
            const res = await axios.post(
                `${API_BASE_URL}/api/comments/${articleId}`,
                { content, parentCommentId },
                config
            );

            if (parentCommentId) {
                // Add reply to existing comment
                setComments(comments.map(comment => {
                    if (comment._id === parentCommentId) {
                        return {
                            ...comment,
                            replies: [...(comment.replies || []), res.data]
                        };
                    }
                    return comment;
                }));
            } else {
                // Add new comment
                setComments([res.data, ...comments]);
            }

            toast.success('Comment posted!');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to post comment');
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete this comment?')) return;

        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.delete(`${API_BASE_URL}/api/comments/${commentId}`, config);

            // Remove from state
            setComments(comments.filter(c => c._id !== commentId).map(comment => ({
                ...comment,
                replies: comment.replies?.filter(r => r._id !== commentId) || []
            })));

            toast.success('Comment deleted');
        } catch (err) {
            toast.error('Failed to delete comment');
        }
    };

    const handleLikeComment = async (commentId) => {
        if (!token) {
            window.location.href = '/login';
            return;
        }

        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.post(
                `${API_BASE_URL}/api/comments/${commentId}/like`,
                {},
                config
            );

            // Update state
            setComments(comments.map(comment => {
                if (comment._id === commentId) {
                    const newLikes = res.data.liked
                        ? [...(comment.likes || []), user.id]
                        : (comment.likes || []).filter(id => id !== user.id);
                    return { ...comment, likes: newLikes, likeCount: res.data.likeCount };
                }
                // Check replies
                if (comment.replies) {
                    return {
                        ...comment,
                        replies: comment.replies.map(reply => {
                            if (reply._id === commentId) {
                                const newLikes = res.data.liked
                                    ? [...(reply.likes || []), user.id]
                                    : (reply.likes || []).filter(id => id !== user.id);
                                return { ...reply, likes: newLikes, likeCount: res.data.likeCount };
                            }
                            return reply;
                        })
                    };
                }
                return comment;
            }));
        } catch (err) {
            toast.error('Failed to like comment');
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-light-primary dark:border-dark-primary"></div>
            </div>
        );
    }

    return (
        <div className="mt-12 space-y-6">
            <h3 className="text-2xl font-bold">
                Comments ({comments.length})
            </h3>

            {/* Comment Form */}
            {user ? (
                <div className="card p-4">
                    <CommentForm onSubmit={handleAddComment} />
                </div>
            ) : (
                <div className="card p-6 text-center">
                    <p className="text-light-text/70 dark:text-dark-text/70 mb-4">
                        Please login to leave a comment
                    </p>
                    <a href="/login" className="btn-primary inline-block">
                        Login
                    </a>
                </div>
            )}

            {/* Comments List */}
            <div className="space-y-2">
                {comments.map(comment => (
                    <CommentItem
                        key={comment._id}
                        comment={comment}
                        onDelete={handleDeleteComment}
                        onLike={handleLikeComment}
                        onReply={handleAddComment}
                        currentUserId={user?.id}
                        currentUserRole={user?.role}
                    />
                ))}

                {comments.length === 0 && (
                    <div className="card p-8 text-center text-light-text/60 dark:text-dark-text/60">
                        No comments yet. Be the first to comment!
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommentsSection;
